import { useState, useRef, useEffect, useCallback } from 'react';
import { bffClient } from '@sluitstraat/core';

interface UseKentekenLookupArgs {
  data: any;
  setData: (updater: (prev: any) => any) => void;
  saveStepData: (id: string, data: any) => void;
  screenId: string;
  t: (key: string) => string;
}

// RDW kenteken (license-plate) lookup: debounced trigger when the plate reaches 6 chars,
// fetches vehicle details, fills the OB_* fields (resetting the value-guarantee toggles so
// they don't leak across vehicles), and clears auto-filled fields when the plate is cleared.
// Extracted verbatim from PersonenAutoScreen.
export function useKentekenLookup({ data, setData, saveStepData, screenId, t }: UseKentekenLookupArgs) {
  const [kentekenInputValue, setKentekenInputValue] = useState('');
  const [isLookingUpKenteken, setIsLookingUpKenteken] = useState(false);
  const [kentekenLookupError, setKentekenLookupError] = useState<string | null>(null);
  const [kentekenDataFilled, setKentekenDataFilled] = useState(false);
  const [kentekenFilledFields, setKentekenFilledFields] = useState<Set<string>>(new Set());
  const kentekenLookupController = useRef<AbortController | null>(null);
  const lastLookedUpKenteken = useRef<string>('');

  const lookupKenteken = useCallback(async (kenteken: string) => {
    const cleaned = kenteken.replace(/-/g, '').toUpperCase();

    // Need exactly 6 characters for a valid Dutch plate
    if (cleaned.length !== 6) return;

    // Skip if already looked up
    if (cleaned === lastLookedUpKenteken.current) return;

    // Abort any pending request
    if (kentekenLookupController.current) {
      kentekenLookupController.current.abort();
    }

    const controller = new AbortController();
    kentekenLookupController.current = controller;

    try {
      setIsLookingUpKenteken(true);
      setKentekenLookupError(null);

      const response = await bffClient.getVehicleDetails(cleaned, controller.signal);
      lastLookedUpKenteken.current = cleaned;

      // Extract vehicle data from the response
      // The BFF returns OB fields under the "Makes" key
      const vehicleData = response?.Makes || response?.Contractdocument?.PP?.OB || response?.OB || response;

      // Check if we got meaningful data (fields are non-empty strings)
      const hasMerk = vehicleData?.OB_MERK && vehicleData.OB_MERK.trim() !== '';
      const hasModel = vehicleData?.OB_MODEL && vehicleData.OB_MODEL.trim() !== '';
      if (!vehicleData || (!hasMerk && !hasModel)) {
        setKentekenLookupError(t('screens.personenauto.errors.vehicleNotFound'));
        setIsLookingUpKenteken(false);
        setKentekenDataFilled(false);
        setKentekenFilledFields(new Set());
        return;
      }

      // Helper: check if a value is non-empty (not null, undefined, or empty string)
      const hasValue = (v: any): boolean => v !== null && v !== undefined && v !== '';
      const asNumber = (v: any): number | undefined => {
        if (!hasValue(v)) return undefined;
        const n = Number(v);
        return isNaN(n) ? undefined : n;
      };

      // Apply vehicle data to form
      setData((prev: any) => {
        const existingOB = prev.Contractdocument?.PP?.OB || {};
        const filledFields: Record<string, any> = { OB_KENTEKE: cleaned };

        // String fields
        if (hasValue(vehicleData.OB_MERK)) filledFields.OB_MERK = vehicleData.OB_MERK;
        if (hasValue(vehicleData.OB_MODEL)) filledFields.OB_MODEL = vehicleData.OB_MODEL;
        if (hasValue(vehicleData.OB_TYPE)) filledFields.OB_TYPE = vehicleData.OB_TYPE;
        if (hasValue(vehicleData.OB_OBJSRT)) filledFields.OB_OBJSRT = vehicleData.OB_OBJSRT;
        if (hasValue(vehicleData.OB_UITVOMS)) filledFields.OB_UITVOMS = vehicleData.OB_UITVOMS;
        if (hasValue(vehicleData.OB_BOUWJR)) filledFields.OB_BOUWJR = String(vehicleData.OB_BOUWJR);
        if (hasValue(vehicleData.OB_BRANSTO)) filledFields.OB_BRANSTO = vehicleData.OB_BRANSTO;
        if (hasValue(vehicleData.OB_BRNDST2)) filledFields.OB_BRNDST2 = vehicleData.OB_BRNDST2;
        if (hasValue(vehicleData.OB_BRNDST3)) filledFields.OB_BRNDST3 = vehicleData.OB_BRNDST3;
        if (hasValue(vehicleData.OB_TRSOORT)) filledFields.OB_TRSOORT = vehicleData.OB_TRSOORT;
        if (hasValue(vehicleData.OB_TURBO)) filledFields.OB_TURBO = vehicleData.OB_TURBO;
        if (hasValue(vehicleData.OB_IMPOBJ)) filledFields.OB_IMPOBJ = vehicleData.OB_IMPOBJ;
        if (hasValue(vehicleData.OB_TNOKLAS)) filledFields.OB_TNOKLAS = vehicleData.OB_TNOKLAS;
        if (hasValue(vehicleData.OB_MLDCODE)) filledFields.OB_MLDCODE = vehicleData.OB_MLDCODE;
        if (hasValue(vehicleData.OB_CARRESS)) filledFields.OB_CARRESS = vehicleData.OB_CARRESS;
        if (hasValue(vehicleData.OB_CARREST)) filledFields.OB_CARREST = vehicleData.OB_CARREST;
        if (hasValue(vehicleData.OB_SEGMNTT)) filledFields.OB_SEGMNTT = vehicleData.OB_SEGMNTT;
        if (hasValue(vehicleData.OB_ABS)) filledFields.OB_ABS = vehicleData.OB_ABS;
        if (hasValue(vehicleData.OB_ELABEL)) filledFields.OB_ELABEL = vehicleData.OB_ELABEL;
        if (hasValue(vehicleData.OB_ACTKLR1)) filledFields.OB_ACTKLR1 = vehicleData.OB_ACTKLR1;
        if (hasValue(vehicleData.OB_ACTKLR2)) filledFields.OB_ACTKLR2 = vehicleData.OB_ACTKLR2;
        if (hasValue(vehicleData.OB_GEBRUIK)) filledFields.OB_GEBRUIK = vehicleData.OB_GEBRUIK;
        if (hasValue(vehicleData.OB_BTWCD)) filledFields.OB_BTWCD = vehicleData.OB_BTWCD;

        // Additional string fields from RDW/kenteken
        if (hasValue(vehicleData.OB_AUTOTLX)) filledFields.OB_AUTOTLX = vehicleData.OB_AUTOTLX;
        if (hasValue(vehicleData.OB_TNOALRM)) filledFields.OB_TNOALRM = vehicleData.OB_TNOALRM;
        if (hasValue(vehicleData.OB_EERSTEI)) filledFields.OB_EERSTEI = vehicleData.OB_EERSTEI;

        // Numeric fields
        const bouwmnd = asNumber(vehicleData.OB_BOUWMND);
        if (bouwmnd !== undefined) filledFields.OB_BOUWMND = bouwmnd;
        const aantkw = asNumber(vehicleData.OB_AANTKW);
        if (aantkw !== undefined) filledFields.OB_AANTKW = aantkw;
        const vwaca = asNumber(vehicleData.OB_VWACA);
        if (vwaca !== undefined) filledFields.OB_VWACA = vwaca;
        const adeur = asNumber(vehicleData.OB_ADEUR);
        if (adeur !== undefined) filledFields.OB_ADEUR = adeur;
        const acceler = asNumber(vehicleData.OB_ACCELER);
        if (acceler !== undefined) filledFields.OB_ACCELER = acceler;
        const maxsnel = asNumber(vehicleData.OB_MAXSNEL);
        if (maxsnel !== undefined) filledFields.OB_MAXSNEL = maxsnel;
        const afmwe = asNumber(vehicleData.OB_AFMWE);
        if (afmwe !== undefined) filledFields.OB_AFMWE = afmwe;
        const zitplaa = asNumber(vehicleData.OB_ZITPLAA);
        if (zitplaa !== undefined) filledFields.OB_ZITPLAA = zitplaa;
        const afmcc = asNumber(vehicleData.OB_AFMCC);
        if (afmcc !== undefined) filledFields.OB_AFMCC = afmcc;
        const dagwrde = asNumber(vehicleData.OB_DAGWRDE);
        if (dagwrde !== undefined) filledFields.OB_DAGWRDE = dagwrde;
        // OB_DATTOEL (first registration date) - numeric YYYYMMDD
        const dattoel = asNumber(vehicleData.OB_DATTOEL);
        if (dattoel !== undefined) filledFields.OB_DATTOEL = dattoel;

        // Track which fields were filled by the lookup (exclude OB_KENTEKE itself)
        const fieldKeys = Object.keys(filledFields).filter(k => k !== 'OB_KENTEKE');
        setTimeout(() => setKentekenFilledFields(new Set(fieldKeys)), 0);

        // Start with a clean OB - only keep OB_KENTEKE and OB_AFMKM (user-specific, not from RDW)
        // This ensures old vehicle data doesn't linger when switching plates
        const cleanOB: Record<string, any> = { OB_KENTEKE: cleaned };
        if (existingOB.OB_AFMKM !== undefined) cleanOB.OB_AFMKM = existingOB.OB_AFMKM;

        const updatedData = {
          ...prev,
          Contractdocument: {
            ...prev.Contractdocument,
            PP: {
              ...prev.Contractdocument?.PP,
              OB: {
                ...cleanOB,
                ...filledFields,
              },
            },
          },
          // Reset value-guarantee toggles so they don't leak across vehicles. RDW lookup can
          // populate OB_EERSTEI directly (without firing the radio's reset hook), and the new
          // car may not qualify for whichever scheme was on previously — leaving NW=J or
          // AKW=J in state would otherwise force CG 2040/2041 into the next request and
          // earn a UWM13/UWM14 rejection.
          Supportsection: {
            ...prev.Supportsection,
            Dekkingen: {
              ...prev.Supportsection?.Dekkingen,
              Nieuwwaarderegeling: 'N',
              Aanschafwaarderegeling: 'N',
            },
          },
        };

        setTimeout(() => saveStepData(screenId, updatedData), 0);
        return updatedData;
      });

      setKentekenDataFilled(true);
      setIsLookingUpKenteken(false);
    } catch (error: any) {
      if (controller.signal.aborted) return;
      setKentekenLookupError(t('screens.personenauto.errors.vehicleLookupFailed'));
      setIsLookingUpKenteken(false);
      setKentekenDataFilled(false);
      setKentekenFilledFields(new Set());
    }
  }, [t, saveStepData, screenId, setData]);

  // Trigger kenteken lookup when plate reaches 6 characters
  useEffect(() => {
    const ob = data?.Contractdocument?.PP?.OB;
    const kenteken = ob?.OB_KENTEKE || '';
    if (kenteken.length === 6) {
      if (!lastLookedUpKenteken.current && ob?.OB_MERK) {
        lastLookedUpKenteken.current = kenteken;
        setKentekenDataFilled(true);
        setKentekenFilledFields(new Set(Object.keys(ob).filter((k) => k !== 'OB_KENTEKE' && k !== 'OB_AFMKM')));
        return;
      }
      const timer = setTimeout(() => lookupKenteken(kenteken), 400);
      return () => clearTimeout(timer);
    } else {
      // Reset when plate is cleared or incomplete
      if (kentekenDataFilled || kentekenFilledFields.size > 0) {
        // Clear all previously auto-filled OB fields so old vehicle data doesn't linger
        setData((prev: any) => {
          const existingOB = prev.Contractdocument?.PP?.OB || {};
          const cleanedOB: Record<string, any> = { OB_KENTEKE: kenteken };
          // Preserve user-entered fields that aren't from RDW
          if (existingOB.OB_AFMKM !== undefined) cleanedOB.OB_AFMKM = existingOB.OB_AFMKM;
          return {
            ...prev,
            Contractdocument: {
              ...prev.Contractdocument,
              PP: { ...prev.Contractdocument?.PP, OB: cleanedOB },
            },
          };
        });
        setKentekenDataFilled(false);
        setKentekenFilledFields(new Set());
        lastLookedUpKenteken.current = '';
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the plate; kentekenDataFilled/kentekenFilledFields are written by this effect
  }, [data?.Contractdocument?.PP?.OB?.OB_KENTEKE, lookupKenteken]);

  return {
    kentekenInputValue,
    setKentekenInputValue,
    isLookingUpKenteken,
    kentekenLookupError,
    kentekenDataFilled,
  };
}
